import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";
import { useNavigate } from "react-router";

import ApiKeysEmptyList from "components/ApiKeys/EmptyList";
import ListContentWrapper from "components/ListContentWrapper";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import { ApiKey } from "types/generated";
import { AccountContext } from "views/AccountWrapper";

import { SettingsContext } from "../Context";
import { getManagementStrategy } from "../helpers";
import ApiKeysEmpty from "./Empty";
import { GET_API_KEYS } from "./gql";
import ApiKeysListV2 from "./List";
import ApiKeysPageLayout from "./PageLayout";

const byMostRecentUsage = (x: ApiKey, y: ApiKey) => (y.lastUsedAt || 0) - (x.lastUsedAt || 0);

const REFETCH_QUERIES = [GET_API_KEYS];

const ApiKeysV2 = () => {
  const { activationStatus } = useTypedContext(SettingsContext);
  const { accountName } = useTypedContext(AccountContext);
  const { personalApiKeys } = useTypedFlags();
  const navigate = useNavigate();

  const { loading, data, error } = useQuery(GET_API_KEYS);

  useTitle(`Organization Settings · API keys · ${accountName}`);

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationApiKeys,
    defaultCallbackTrackProperties: { managementStrategy: getManagementStrategy(activationStatus) },
  });

  const memoizedApiKeys = useMemo<ApiKey[]>(() => {
    return data?.apiKeys ? [...data.apiKeys].sort(byMostRecentUsage) : [];
  }, [data]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data) {
    return <PageLoading />;
  }

  if (!data) {
    return <NotFoundPage />;
  }

  const openCreateApiKey = () => {
    trackSegmentAnalyticsEvent("Create Key");
    navigate("/settings/new/api-key");
  };

  return (
    <ApiKeysPageLayout
      openCreateApiKey={openCreateApiKey}
      hasApiKeys={memoizedApiKeys.length > 0}
      showPersonalApiKeysCallout
    >
      <ListContentWrapper>
        {memoizedApiKeys.length > 0 && (
          <ApiKeysListV2
            apiKeys={memoizedApiKeys}
            refetchQueriesOnDelete={REFETCH_QUERIES}
            refetchQueriesOnUpdate={REFETCH_QUERIES}
          />
        )}
        {memoizedApiKeys.length === 0 && personalApiKeys && <ApiKeysEmpty />}
        {memoizedApiKeys.length === 0 && !personalApiKeys && <ApiKeysEmptyList />}
      </ListContentWrapper>
    </ApiKeysPageLayout>
  );
};

export default ApiKeysV2;
